import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Clock, Languages, Sparkles, MessageCircle } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useAuth } from "../contexts/AuthContext";

function useMidnightCountdown() {
  const calc = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    return Math.max(0, Math.floor((midnight - now) / 1000));
  };
  const [sec, setSec] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setSec(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  const h = String(Math.floor(sec / 3600)).padStart(2, "0");
  const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
  const s = String(sec % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

// "1 FREE AI Chat Question" banner. Routes to /chat when a Kundli already
// exists in this session, otherwise to /kundli so the user enters birth
// details first (chat needs a chart).
export const FreeChatBanner = ({ compact = false, onNeedKundli }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const hasReport = typeof window !== "undefined" && !!window.localStorage.getItem("astrofate_report");
  const go = () => {
    if (hasReport) navigate("/chat");
    else if (onNeedKundli) onNeedKundli();
    else navigate("/kundli");
  };
  if (compact) {
    return (
      <button data-testid="promo-free-chat-cta" onClick={go} style={{
        display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer",
        background: "linear-gradient(135deg, rgba(255,209,102,0.14), rgba(255,209,102,0.04))",
        border: "1px solid rgba(255,209,102,0.35)", borderRadius: "50px", padding: "8px 18px",
        color: "#FFD166", fontWeight: 700, fontSize: "0.85rem", fontFamily: "'DM Sans', sans-serif"
      }}>
        <Gift size={15} /> {t("promo.freeChatTitle")} →
      </button>
    );
  }
  return (
    <div data-testid="promo-free-chat-banner" className="promo-banner-free" style={{
      background: "linear-gradient(135deg, rgba(255,209,102,0.12), rgba(157,78,221,0.06))",
      border: "1px solid rgba(255,209,102,0.3)", borderRadius: "16px", padding: "18px 22px",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", flexWrap: "wrap"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
        <div style={{ width: "42px", height: "42px", borderRadius: "12px", flexShrink: 0, background: "rgba(255,209,102,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFD166" }}>
          <Gift size={20} />
        </div>
        <div>
          <p style={{ color: "#FFD166", fontWeight: 700, fontSize: "0.98rem" }}>{t("promo.freeChatTitle")}</p>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", marginTop: "2px" }}>
            {hasReport ? t("promo.freeChatDescReady") : t("promo.freeChatDesc")}
          </p>
        </div>
      </div>
      <button data-testid="promo-free-chat-cta" onClick={go} className="btn-gold" style={{ padding: "9px 20px", fontSize: "0.85rem", flexShrink: 0 }}>
        {t("promo.freeChatCta")}
      </button>
    </div>
  );
};

// Consultation ad — 50% OFF ₹249 (was ₹501) with a daily-reset countdown.
export const ConsultOfferBanner = ({ compact = false }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { price, priceOld } = useCurrency();
  const timer = useMidnightCountdown();
  if (compact) {
    return (
      <div data-testid="promo-consult-banner" style={{
        background: "linear-gradient(135deg, rgba(157,78,221,0.14), rgba(45,11,69,0.25))",
        border: "1px solid rgba(157,78,221,0.35)", borderRadius: "14px", padding: "13px 18px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <span style={{ background: "linear-gradient(135deg, #ef4444, #f97316)", color: "white", fontSize: "0.66rem", fontWeight: 800, letterSpacing: "0.5px", padding: "3px 10px", borderRadius: "20px" }}>{t("promo.consultBadge")}</span>
          <span style={{ color: "white", fontWeight: 700, fontSize: "0.88rem" }}>{t("promo.consultTitleShort")}</span>
          <span style={{ color: "#FFD166", fontWeight: 800, fontSize: "0.95rem" }}>{price("consultation")}</span>
          <span style={{ color: "rgba(255,255,255,0.35)", textDecoration: "line-through", fontSize: "0.82rem" }}>{priceOld("consultation")}</span>
        </div>
        <button data-testid="promo-consult-cta" onClick={() => navigate("/consultation")} className="btn-purple" style={{ padding: "8px 18px", fontSize: "0.82rem", flexShrink: 0 }}>
          {t("promo.consultCtaShort", { price: price("consultation") })}
        </button>
      </div>
    );
  }
  return (
    <div data-testid="promo-consult-banner" className="glass-card promo-offer" style={{
      background: "linear-gradient(135deg, rgba(157,78,221,0.16), rgba(45,11,69,0.35))",
      border: "1px solid rgba(157,78,221,0.4)", borderRadius: "18px", padding: "26px 28px",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "18px", flexWrap: "wrap"
    }}>
      <div style={{ minWidth: 0, flex: "1 1 320px" }}>
        <span style={{ background: "linear-gradient(135deg, #ef4444, #f97316)", color: "white", fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.6px", padding: "4px 12px", borderRadius: "20px", display: "inline-flex", alignItems: "center", gap: "5px" }}>
          <Sparkles size={11} /> {t("promo.consultBadge")}
        </span>
        <h3 className="font-cinzel" style={{ color: "white", fontSize: "1.15rem", margin: "12px 0 6px" }}>{t("promo.consultTitle")}</h3>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.86rem", lineHeight: 1.6 }}>{t("promo.consultDesc")}</p>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", color: "rgba(255,255,255,0.5)", fontSize: "0.78rem" }}>
          <Clock size={13} style={{ color: "#fca5a5" }} />
          {t("promo.consultEnds")}
          <span data-testid="promo-consult-countdown" style={{ color: "#fca5a5", fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>{timer}</span>
        </div>
      </div>
      <div className="promo-price-wrap" style={{ textAlign: "center", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "8px", justifyContent: "center" }}>
          <span className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.9rem", fontWeight: 700 }}>{price("consultation")}</span>
          <span style={{ color: "rgba(255,255,255,0.35)", textDecoration: "line-through", fontSize: "1rem" }}>{priceOld("consultation")}</span>
        </div>
        <button data-testid="promo-consult-cta" onClick={() => navigate("/consultation")} className="btn-purple" style={{ marginTop: "10px", padding: "11px 26px", fontSize: "0.9rem" }}>
          {t("promo.consultCta", { price: price("consultation") })}
        </button>
      </div>
    </div>
  );
};

// AI Chat plan ad — ₹51 for 4 questions, gold theme, pairs with ConsultOfferBanner.
export const AiChatOfferBanner = ({ compact = false }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { price } = useCurrency();
  if (compact) {
    return (
      <div data-testid="promo-aichat-banner" style={{
        background: "linear-gradient(135deg, rgba(255,209,102,0.13), rgba(45,11,69,0.2))",
        border: "1px solid rgba(255,209,102,0.35)", borderRadius: "14px", padding: "13px 18px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <span style={{ background: "linear-gradient(135deg, #F4B942, #FFD166)", color: "#120021", fontSize: "0.66rem", fontWeight: 800, letterSpacing: "0.5px", padding: "3px 10px", borderRadius: "20px" }}>{t("promo.aiChatBadge")}</span>
          <span style={{ color: "white", fontWeight: 700, fontSize: "0.88rem" }}>{t("promo.aiChatTitleShort")}</span>
          <span style={{ color: "#FFD166", fontWeight: 800, fontSize: "0.95rem" }}>{price("ai_chat")}</span>
        </div>
        <button data-testid="promo-aichat-cta" onClick={() => navigate("/chat")} className="btn-gold" style={{ padding: "8px 18px", fontSize: "0.82rem", flexShrink: 0 }}>
          {t("promo.aiChatCtaShort", { price: price("ai_chat") })}
        </button>
      </div>
    );
  }
  return (
    <div data-testid="promo-aichat-banner" className="glass-card promo-offer" style={{
      background: "linear-gradient(135deg, rgba(255,209,102,0.13), rgba(45,11,69,0.3))",
      border: "1px solid rgba(255,209,102,0.4)", borderRadius: "18px", padding: "26px 28px",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "18px", flexWrap: "wrap"
    }}>
      <div style={{ minWidth: 0, flex: "1 1 260px" }}>
        <span style={{ background: "linear-gradient(135deg, #F4B942, #FFD166)", color: "#120021", fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.6px", padding: "4px 12px", borderRadius: "20px", display: "inline-flex", alignItems: "center", gap: "5px" }}>
          <MessageCircle size={11} /> {t("promo.aiChatBadge")}
        </span>
        <h3 className="font-cinzel" style={{ color: "white", fontSize: "1.15rem", margin: "12px 0 6px" }}>{t("promo.aiChatTitle")}</h3>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.86rem", lineHeight: 1.6 }}>{t("promo.aiChatDesc")}</p>
      </div>
      <div className="promo-price-wrap" style={{ textAlign: "center", flexShrink: 0 }}>
        <div className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.9rem", fontWeight: 700 }}>{price("ai_chat")}</div>
        <button data-testid="promo-aichat-cta" onClick={() => navigate("/chat")} className="btn-gold" style={{ marginTop: "10px", padding: "11px 26px", fontSize: "0.9rem" }}>
          {t("promo.aiChatCta", { price: price("ai_chat") })}
        </button>
      </div>
    </div>
  );
};

// Conversion funnel stage: 1 = free chat, 2 = ₹51 AI plan, 3 = ₹249 consult, 0 = hide.
export function useOfferStage() {
  const { user } = useAuth();
  if (user?.role === "admin" || user?.plan === "consultation") return 0;
  if (user?.plan === "ai_chat") return 3;
  if (user?.free_question_used) return 2;
  return 1;
}

// Renders exactly ONE offer matching the visitor's funnel stage.
export const SmartOfferBanner = ({ compact = false }) => {
  const stage = useOfferStage();
  if (stage === 1) return <FreeChatBanner compact={compact} />;
  if (stage === 2) return <AiChatOfferBanner compact={compact} />;
  if (stage === 3) return <ConsultOfferBanner compact={compact} />;
  return null;
};

// Homepage-only strip advertising Hindi. Hidden once user is already on Hindi.
export const HindiPromoBanner = () => {
  const { lang, setLang } = useI18n();
  if (lang === "hi") return null;
  // One compact Hindi-only pill — short, unmissable, and it barely costs any
  // vertical space above the hero.
  return (
    <div style={{ textAlign: "center" }}>
    <div data-testid="promo-hindi-banner" className="hindi-strip" style={{
      background: "linear-gradient(90deg, rgba(255,153,51,0.16), rgba(19,136,8,0.12))",
      border: "1px solid rgba(255,209,102,0.3)", borderRadius: "50px", padding: "5px 6px 5px 15px",
      display: "inline-flex", alignItems: "center", gap: "10px", margin: "0 auto 20px",
    }}>
      <Languages size={15} style={{ color: "#FFD166", flexShrink: 0 }} />
      <span lang="hi" style={{ color: "#FFD166", fontSize: "0.84rem", fontWeight: 700, whiteSpace: "nowrap" }}>
        अब हिंदी में भी
      </span>
      <button data-testid="promo-hindi-cta" onClick={() => setLang("hi")} lang="hi" style={{
        background: "linear-gradient(135deg, #F4B942, #FFD166)", color: "#120021", border: "none",
        borderRadius: "50px", padding: "6px 15px", fontWeight: 800, fontSize: "0.8rem", cursor: "pointer",
        flexShrink: 0, whiteSpace: "nowrap",
      }}>
        हिंदी में देखें →
      </button>
    </div>
    </div>
  );
};
